import { useState, useEffect, useCallback, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { AuthContext } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getNationByCode } from "@/data/hockeyNations";
import { useOlympicsSchedule } from "@/hooks/useOlympicsSchedule";
import { useToast } from "@/hooks/use-toast";
import type { OlympicGame } from "@/data/olympics2026Schedule";

const NATION_ALERTS_ENABLED_KEY = "hockey_nation_alerts_enabled";
const NATION_ALERTS_NOTIFIED_KEY = "hockey_nation_alerts_notified";
const NOTIFICATION_LEAD_TIME = 30 * 60 * 1000; // 30 minutes before game

// Map ISO codes to country names used in Olympics schedule
const getNationName = (isoCode: string): string | null => {
  const mapping: Record<string, string> = {
    CAN: "Canada",
    USA: "United States",
    SWE: "Sweden",
    FIN: "Finland",
    RUS: "Russia",
    CZE: "Czechia",
    CHE: "Switzerland",
    DEU: "Germany",
    SVK: "Slovakia",
    LVA: "Latvia",
    DNK: "Denmark",
    NOR: "Norway",
    FRA: "France",
    AUT: "Austria",
    KAZ: "Kazakhstan",
    JPN: "Japan",
  };
  return mapping[isoCode] || null;
};

export interface UpcomingNationGame {
  id: string;
  homeTeam: string;
  awayTeam: string;
  homeFlag: string;
  awayFlag: string;
  date: string;
  time: string;
  stage: string;
  venue: string;
  tournament: "women" | "men";
  startsAt: Date;
}

export function useFavoriteNationAlerts() {
  const [alertsEnabled, setAlertsEnabled] = useState(() => {
    return localStorage.getItem(NATION_ALERTS_ENABLED_KEY) === "true";
  });
  const [notifiedGames, setNotifiedGames] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(NATION_ALERTS_NOTIFIED_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const { toast } = useToast();

  // Get user's favorite nation
  const authContext = useContext(AuthContext);
  const user = authContext?.user ?? null;

  const { data: userProfile } = useQuery({
    queryKey: ["userProfile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("favorite_nation")
        .eq("user_id", user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const favoriteNation = getNationByCode(userProfile?.favorite_nation ?? null);
  const favoriteNationName = userProfile?.favorite_nation
    ? getNationName(userProfile.favorite_nation)
    : null;

  // Get Olympics schedule
  const { data: olympicsData } = useOlympicsSchedule();

  // Check notification permission
  useEffect(() => {
    if ("Notification" in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!("Notification" in window)) {
      return false;
    }

    if (Notification.permission === "granted") {
      setPermission("granted");
      return true;
    }

    if (Notification.permission !== "denied") {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === "granted";
    }

    return false;
  }, []);

  // Toggle alerts
  const toggleAlerts = useCallback(async () => {
    if (!alertsEnabled) {
      const hasPermission = await requestPermission();
      if (!hasPermission) {
        toast({
          title: "Notifications required",
          description: "Please enable browser notifications to receive game alerts.",
          variant: "destructive",
        });
        return;
      }
    }

    const newState = !alertsEnabled;
    setAlertsEnabled(newState);
    localStorage.setItem(NATION_ALERTS_ENABLED_KEY, String(newState));

    if (newState && favoriteNation) {
      toast({
        title: `${favoriteNation.flag} Alerts enabled!`,
        description: `You'll be notified 30 minutes before ${favoriteNation.name} games.`,
      });
    } else {
      toast({
        title: "Alerts disabled",
        description: "You won't receive automatic game notifications.",
      });
    }
  }, [alertsEnabled, favoriteNation, requestPermission, toast]);

  // Get upcoming games for favorite nation
  const getUpcomingNationGames = useCallback((): UpcomingNationGame[] => {
    if (!favoriteNationName || !olympicsData) return [];

    const allGames = [...olympicsData.womensSchedule, ...olympicsData.mensSchedule];
    const now = new Date();

    return allGames
      .filter((game) => {
        // Check if nation is playing
        const isPlaying = game.homeTeam === favoriteNationName || game.awayTeam === favoriteNationName;
        if (!isPlaying) return false;

        // Check if game is in the future
        const gameDateTime = new Date(`${game.date}T${game.time}:00+01:00`); // CET
        return gameDateTime > now;
      })
      .map((game) => ({
        ...game,
        startsAt: new Date(`${game.date}T${game.time}:00+01:00`),
      }))
      .sort((a, b) => a.startsAt.getTime() - b.startsAt.getTime());
  }, [favoriteNationName, olympicsData]);

  const upcomingGames = getUpcomingNationGames();

  // Check for upcoming games and notify
  useEffect(() => {
    if (!alertsEnabled || permission !== "granted" || !favoriteNation) return;

    const checkInterval = setInterval(() => {
      const now = Date.now();
      const upcoming = getUpcomingNationGames();

      upcoming.forEach((game) => {
        const timeUntilGame = game.startsAt.getTime() - now;

        // Notify if within 30 minutes and not already notified
        if (timeUntilGame <= NOTIFICATION_LEAD_TIME && timeUntilGame > 0 && !notifiedGames.has(game.id)) {
          // Show browser notification
          new Notification(`${favoriteNation.flag} ${favoriteNation.name} Playing Soon!`, {
            body: `${game.awayTeam} vs ${game.homeTeam} starts at ${game.time} CET\n${game.stage} - ${game.tournament === "women" ? "Women's" : "Men's"} Tournament`,
            icon: "/favicon.ico",
            tag: `nation-game-${game.id}`,
            requireInteraction: true,
          });

          // Show in-app toast
          toast({
            title: `${favoriteNation.flag} ${favoriteNation.name} Playing Soon!`,
            description: `${game.awayTeam} vs ${game.homeTeam} at ${game.time} CET`,
          });

          // Mark as notified
          setNotifiedGames((prev) => {
            const updated = new Set(prev);
            updated.add(game.id);
            localStorage.setItem(NATION_ALERTS_NOTIFIED_KEY, JSON.stringify([...updated]));
            return updated;
          });
        }
      });
    }, 60000); // Check every minute

    return () => clearInterval(checkInterval);
  }, [alertsEnabled, permission, favoriteNation, getUpcomingNationGames, notifiedGames, toast]);

  return {
    alertsEnabled,
    toggleAlerts,
    favoriteNation,
    favoriteNationName,
    upcomingGames,
    permission,
    requestPermission,
    isSupported: "Notification" in window,
    hasNation: !!favoriteNation,
  };
}
